import { Injectable } from "@angular/core";
import { CanActivate, Router, UrlTree } from "@angular/router";
import { HttpErrorResponse } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { PermissionCheckerService } from "./services/permission/permission-checker.service";
import { ApplicationConfigService } from "./services/application-config.service";
import { AccountantProfileControllerService, ClientProfileControllerService } from "./api/accounting-service";

/**
 * Implemented as CanActivate (not CanMatch) so it re-evaluates on every
 * navigation. With CanMatch the guard runs once when the route module is
 * matched; if permissions hadn't loaded yet the result was cached as `false`
 * and subsequent dashboard-icon clicks silently dropped on the floor.
 */
@Injectable({ providedIn: 'root' })
export class HomeRedirectGuard implements CanActivate {

  constructor(private router: Router,
              private permissionChecker : PermissionCheckerService,
              private appConfig : ApplicationConfigService,
              private clientProfileApi : ClientProfileControllerService,
              private accountantProfileApi : AccountantProfileControllerService) {}

  canActivate(): Observable<UrlTree | boolean> | UrlTree | boolean {
    // TODO NUC-269: persona landing redirect. Analogous to the reference
    // firstPermittedRoute() (capability-priority landing), but bookwise's landing
    // targets are persona dashboards (client/accountant/admin) with no single
    // capability that distinguishes them. Left role-based pending a persona→landing
    // capability-priority map.

    // CLIENT: route a first-time user (no profile yet) through the onboarding
    // wizard; everyone else lands on their dashboard. GET /v1/client-profiles/{id}
    // 404s until the profile exists, so 404 == "not onboarded". Once onboarding
    // saves the profile the GET succeeds and this stops routing to /onboarding.
    if (this.permissionChecker.hasRoleClient()) {
      return this.clientProfileApi.findClientProfileById(this.appConfig.getUserId()).pipe(
        map(profile => (profile && profile.id)
          ? this.router.parseUrl('/client/dashboard')
          : this.router.parseUrl('/onboarding')),
        // 404 → onboarding; any OTHER error fails open to the dashboard so a blip in
        // the profile service never traps a real client in onboarding.
        catchError((err: unknown) => of(
          (err instanceof HttpErrorResponse && err.status === 404)
            ? this.router.parseUrl('/onboarding')
            : this.router.parseUrl('/client/dashboard')))
      );
    }
    // ACCOUNTANT: same first-login onboarding gate as clients, against the accountant profile.
    if (this.permissionChecker.hasRoleAccountant()) {
      return this.accountantProfileApi.findAccountantProfileById(this.appConfig.getUserId()).pipe(
        map(profile => (profile && profile.id)
          ? this.router.parseUrl('/accountant/dashboard')
          : this.router.parseUrl('/onboarding')),
        catchError((err: unknown) => of(
          (err instanceof HttpErrorResponse && err.status === 404)
            ? this.router.parseUrl('/onboarding')
            : this.router.parseUrl('/accountant/dashboard')))
      );
    }
    if (this.permissionChecker.hasRoleAdmin())      return this.router.parseUrl('/administration/dashboard');
    if (this.permissionChecker.hasRoleAuditor())    return true;
    // No app access, but a DevOps operator → this user belongs to the ops
    // console, not the tenant app (e.g. they followed a password-reset link
    // here). Bounce them to the console deployment at console.<host> instead of
    // dead-ending on access-denied. External redirect (separate app + BFF).
    if (this.permissionChecker.hasRoleDevOps()) {
      // Console URL is configurable (BFF env CONSOLE_URL, surfaced via
      // /bootstrap); fall back to the console.<host> convention if unset.
      const consoleUrl = this.appConfig.getConsoleUrl() || `https://console.${window.location.host}`;
      window.location.href = consoleUrl;
      return false;
    }
    return this.router.parseUrl('/access-denied');
  }
}
